import {calculateMergeability} from '../../../helpers/mergeability-helpers'
import {EffectiveEntryStatus} from '../../../helpers/effective-entry-status'
import type {Status} from '../../../helpers/mergeability-status'
import type {StackSectionStackEntry} from '../../../helpers/merge-box-status-calculator/stack-section-types'
import {
  MergeAction,
  type AdvisoryWorkspace,
  type AutoMergeRequest,
  type MergeStateStatus,
  type PullRequestMergeRequirementsState,
  type ViewerMergeActions,
} from '../../../types'

export type MergeControlVariant =
  | {type: 'disable-auto-merge'}
  | {type: 'inactive-direct'}
  | {type: 'merge-queue-bypass'}
  | {type: 'merge-queue'}
  | {type: 'auto-merge'; isAutoMergeAllowed: boolean}
  | {type: 'direct'}

type AbsentMergeControlModel = {
  visibility: 'absent'
  isMergeable: boolean
}

export type RenderedMergeControlModel = {
  visibility: 'hidden' | 'visible'
  control: MergeControlVariant
  isBypassAllowed: boolean
  isMergeable: boolean
  isMergeQueueEnabled: boolean
  shouldRenderBypassToggle: boolean
  stackPositionInStack?: number
  stackSize?: number
}

export type MergeControlModel = AbsentMergeControlModel | RenderedMergeControlModel

export type DeriveMergeControlModelInput = {
  advisoryWorkspace: AdvisoryWorkspace
  autoMergeRequest: AutoMergeRequest
  isAdminBypassToggleChecked: boolean
  isConfirmingMergeInfo: boolean
  isPullRequestStacksAsyncMergeEnabled: boolean
  isStackMergeInProgress: boolean
  isStacked?: boolean
  isStackedPrAdminBypassEnabled: boolean
  mergeRequirementsState: PullRequestMergeRequirementsState
  mergeStateStatus: MergeStateStatus
  stackedEntries?: StackSectionStackEntry[]
  stackedEntriesToMergeCount?: number
  status: Status
  viewerCanAdminBypassMergeRequirements: boolean
  viewerCanEnableAutoMerge: boolean
  viewerMergeActions: ViewerMergeActions
}

export function deriveMergeControlModel({
  advisoryWorkspace,
  autoMergeRequest,
  isAdminBypassToggleChecked,
  isConfirmingMergeInfo,
  isPullRequestStacksAsyncMergeEnabled,
  isStackMergeInProgress,
  isStacked,
  isStackedPrAdminBypassEnabled,
  mergeRequirementsState,
  mergeStateStatus,
  stackedEntries,
  stackedEntriesToMergeCount,
  status,
  viewerCanAdminBypassMergeRequirements,
  viewerCanEnableAutoMerge,
  viewerMergeActions,
}: DeriveMergeControlModelInput): MergeControlModel {
  const isMergeable = calculateMergeability(mergeRequirementsState, status, mergeStateStatus)
  const isAsyncStackMerge = isStacked === true && (isAdminBypassToggleChecked || isPullRequestStacksAsyncMergeEnabled)

  // Do not render controls in an advisory workspace or while a synchronous stack merge is in progress.
  if (advisoryWorkspace || (isStackMergeInProgress && !isAsyncStackMerge)) {
    return {visibility: 'absent', isMergeable}
  }

  const isMergeQueueEnabled =
    viewerMergeActions.find(({name}) => name === MergeAction.MERGE_QUEUE)?.allowableStatus === 'ALLOWED'
  const isDirectMergeEnabled =
    viewerMergeActions.find(({name}) => name === MergeAction.DIRECT_MERGE)?.allowableStatus === 'ALLOWED'
  const isAutoMergeActive = !!autoMergeRequest
  const stackSize = stackedEntries?.length
  const stackPosition = stackedEntries?.findIndex(entry => entry.position === 'CURRENT')
  const stackPositionInStack = stackPosition !== undefined && stackPosition >= 0 ? stackPosition + 1 : undefined

  // If an AutoMerge request is present, we do not show the bypass checkbox.
  // Stacked PRs only ever show the bypass checkbox for the bottom-most unmerged
  // entry in the stack. Queued entries are excluded from stackedEntriesToMergeCount,
  // so check them separately before treating a count of one as the bottom-most entry.
  // When merge queue is enabled, the ability to bypass the merge requirements does not depend on mergeability state.
  const isStackedPrEligibleForBypass =
    !isStacked ||
    (stackedEntriesToMergeCount === 1 &&
      !stackedEntries?.some(
        entry => entry.position === 'BEFORE' && entry.effectiveEntryStatus === EffectiveEntryStatus.Queued,
      ) &&
      isStackedPrAdminBypassEnabled)

  // Active auto-merge requests never offer bypass. When merge queue is enabled, bypass eligibility does not depend
  // on mergeability. Otherwise, mergeability keeps the toggle responsive to live status-check updates.
  const isBypassAllowed =
    viewerCanAdminBypassMergeRequirements &&
    !isAutoMergeActive &&
    isStackedPrEligibleForBypass &&
    (isMergeQueueEnabled || !isMergeable)

  const commonModel = {
    visibility: isStackMergeInProgress && isAsyncStackMerge ? ('hidden' as const) : ('visible' as const),
    isBypassAllowed,
    isMergeable,
    isMergeQueueEnabled,
    shouldRenderBypassToggle: isBypassAllowed && !isConfirmingMergeInfo,
    stackPositionInStack,
    stackSize,
  }

  // Auto-merge is active because a request is present.
  if (isAutoMergeActive) {
    return {...commonModel, control: {type: 'disable-auto-merge'}}
  }

  // Neither merge path is currently available.
  if (!isMergeQueueEnabled && !isDirectMergeEnabled) {
    return {...commonModel, control: {type: 'inactive-direct'}}
  }

  // Merge queue is enabled, but the user can bypass merge queue and did bypass merge queue, so we let them direct merge
  if (isMergeQueueEnabled && isBypassAllowed && isAdminBypassToggleChecked) {
    return {...commonModel, control: {type: 'merge-queue-bypass'}}
  }

  // Merge queue is enabled but the user hasn't elected to bypass it (or possibly cannot bypass it)
  if (isMergeQueueEnabled) {
    return {...commonModel, control: {type: 'merge-queue'}}
  }

  // Auto merge is allowed and the user hasn't bypassed it to direct merge.
  // Stacked PRs do not support auto-merge, so they fall through to the disabled merge button below.
  if (viewerCanEnableAutoMerge && !isAdminBypassToggleChecked && !isStacked) {
    return {
      ...commonModel,
      control: {type: 'auto-merge', isAutoMergeAllowed: !isMergeable},
    }
  }

  return {...commonModel, control: {type: 'direct'}}
}
